<template>
    <section class="faq-accordion" :style="`background-color: ${blok.BackgroundColor?.color};`">
        <div class="container mx-auto">
            <component
                v-if="isTitleAsComponent"
                :is="titleBlockComponent.component || 'div'"
                :blok="titleBlockComponent"
            />
            <h2 class="simple-text-title" v-else>{{ blok.title }}</h2>
            <div class="subtitle" v-html="blok.subtitle"></div>
            <div class="accordions">
                <Accordion
                    v-for="item in accordionsData"
                    :key="item._uid"
                    :is-open="item.isOpen as boolean"
                    @toggle="handleToggle(item._uid)"
                >
                    <template #title>{{ item.title }}</template>
                    <template #icon>
                        <template v-if="!withArrows">
                            <NuxtIcon v-if="!item.isOpen" name="ri:add-line" />
                            <NuxtIcon v-else name="ri:subtract-line" />
                        </template>
                    </template>
                    <template #body>
                        <rich-text class="rich-text" :content="item.richTextContent" />
                    </template>
                </Accordion>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import Accordion from './Accordion.vue';
import type { IFaqSection, IAccordionState } from '@nuxt-monorepo/ui/types/components/Faqs';
import type { ISchemaFaqPage } from '@nuxt-monorepo/ui/types/components/Schema/FaqPage';

const { blok } = defineProps<{ blok: IFaqSection; withArrows?: false }>();

const { render } = useStoryblokRichText({});
const richText = (content: any) => {
    return render(content.content);
};

const accordionsState: IAccordionState[] = blok.questions.map((item) => ({
    _uid: item._uid,
    isOpen: false,
    richTextContent: item.content,
}));
const accordions: Ref<IAccordionState[]> = ref(accordionsState);

const accordionsData = computed(() => {
    return blok.questions.map((question) => ({
        ...question,
        ...accordions.value.find((accordion) => question._uid === accordion._uid),
    }));
});

const handleToggle = (toggleId: string) => {
    accordions.value = accordions.value.map((accordion) => {
        accordion.isOpen = toggleId === accordion._uid ? !accordion.isOpen : false;
        return accordion;
    });
};

const titleBlockComponent = computed(() => blok.titleComponent?.[0] ?? null);

const isTitleAsComponent = computed<boolean>(() => blok.headingType === 'component' && !!titleBlockComponent.value);

if (import.meta.server) {
    const schema: ISchemaFaqPage = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [],
    };

    blok.questions.forEach((item: IFaq) => {
        const entity = {
            '@type': 'Question',
            name: item.title,
            acceptedAnswer: {
                '@type': 'Answer',
                text: renderRichText(item.content).replace(/<[^>]*>?/gm, ''),
            },
        };
        schema.mainEntity.push(entity);
    });

    if (schema.mainEntity.length > 0) {
        // Set Schema List to <Head>
        useHead({
            script: [{ type: 'application/ld+json', innerHTML: schema }],
        });
    }
}
</script>

<style scoped lang="postcss">
.faq-accordion {
    @apply py-12 px-4 md:px-0;
}

h1,
h2,
h3,
h4,
h5,
h6 {
    @apply text-[#343a40] font-normal text-center leading-[normal] mb-6;
}

h2.simple-text-title {
    @apply text-[40px];
}

.subtitle {
    @apply text-xl leading-[29px] text-gray-800 font-normal text-center mb-12;
}

.accordions {
    @apply border-t-[#eee] border-t;
}

.accordions .accordion {
    @apply p-4 border-b-[#eee] border-b;
    border-bottom-style: solid;

    &:deep(.accordion-header) {
        @apply flex justify-between text-gray-800 font-semibold select-none cursor-pointer text-base md:text-xl;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        .iconify {
            @apply text-base;
        }
    }
    &:deep(.accordion-body) {
        @apply text-base md:text-lg text-[#585d63];
        div > p:not(:first-of-type) {
            @apply mr-[28px];
        }

        p {
            @apply mb-4;
        }

        .rich-text ul p {
            @apply mb-0;
        }

        .rich-text ol p {
            @apply mb-0;
        }

        a {
            @apply text-primary hover:text-primary-700 hover:underline;
        }
    }

    &.active {
        &:deep(.accordion-header) {
            @apply text-[#0094ff];
        }
    }
}
</style>
